import { ClientType } from 'storeTypes'
import {
    buildPhoneMatchRegex,
    normalizePhone,
    toComparablePhone
} from './phoneHelpers'

export const getDefaultClientContacts = (client: ClientType): string[] =>
    Array.isArray(client.default_client_contacts)
        ? client.default_client_contacts
        : []

export const isDefaultClientForPhone = (
    client: ClientType,
    phone: string
): boolean => {
    const comparable = toComparablePhone(phone)
    if (!comparable) return false
    return getDefaultClientContacts(client).some(
        (contact) => toComparablePhone(contact) === comparable
    )
}

/**
 * Pick a client from phone-search matches.
 * Only clients whose default_client_contacts include the phone are eligible.
 * An empty default_client_contacts list means the client is not the default.
 */
export const selectClientFromPhoneMatches = (
    clients: ClientType[],
    phone: string
): ClientType | undefined => {
    if (!clients.length || !phone) return undefined
    return clients.find((client) => isDefaultClientForPhone(client, phone))
}

export const defaultClientContactsMatchQuery = (
    client: ClientType,
    digits: string
): boolean => {
    if (!digits) return false
    const fuzzyRegex = buildPhoneMatchRegex(digits)
    return getDefaultClientContacts(client).some((contact) =>
        fuzzyRegex.test(normalizePhone(contact))
    )
}

export const matchingDefaultContact = (
    client: ClientType,
    phoneInput: string
): string => {
    const contacts = getDefaultClientContacts(client)
    const comparable = toComparablePhone(phoneInput)
    const exact = contacts.find(
        (contact) => toComparablePhone(contact) === comparable
    )
    if (exact) return toComparablePhone(exact)

    const digits = normalizePhone(phoneInput)
    if (digits) {
        const fuzzyRegex = buildPhoneMatchRegex(digits)
        const fuzzy = contacts.find((contact) =>
            fuzzyRegex.test(normalizePhone(contact))
        )
        if (fuzzy) return toComparablePhone(fuzzy)
    }

    return toComparablePhone(contacts[0] || '')
}

/**
 * Seed `default_client_contacts` for a client record created locally.
 *
 * Phone matching (bulk enrolment, client search) only looks at
 * `default_client_contacts`, which Core Services computes. A client created
 * offline or before the next village sync has no such list yet, so it could
 * never be matched by its own phone. Mark it as the default for its phone
 * locally; the next sync from Core Services replaces it with the real value.
 */
export const seedDefaultClientContacts = <
    T extends Pick<ClientType, 'phone' | 'default_client_contacts'>
>(
    client: T
): T => {
    if (
        Array.isArray(client.default_client_contacts) &&
        client.default_client_contacts.length > 0
    ) {
        return client
    }
    const phone = toComparablePhone(client.phone || '')
    if (!phone) return client
    return { ...client, default_client_contacts: [phone] }
}

/**
 * Prepare a client record before an IndexedDB put/add that replaces the
 * whole object (enrollment / orders writes).
 *
 * Incoming payloads usually have no `default_client_contacts`. Keep an
 * existing list when the client is already stored; otherwise seed from
 * phone so bulk enrolment search can find a locally created client.
 */
export const withDefaultClientContactsForLocalWrite = <
    T extends Pick<ClientType, 'phone' | 'default_client_contacts'>
>(
    incoming: T,
    existing?: Pick<ClientType, 'default_client_contacts'> | null
): T => {
    const incomingContacts = incoming.default_client_contacts
    const contacts =
        Array.isArray(incomingContacts) && incomingContacts.length > 0
            ? incomingContacts
            : existing?.default_client_contacts

    return seedDefaultClientContacts({
        ...incoming,
        default_client_contacts: contacts
    })
}
